import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Package, Recycle, ShieldCheck, Scissors, Layers, ChevronDown, Check } from "lucide-react";

const CATEGORIES = [
  { label: "Carton déménagement", to: "/cartons-demenagement", Icon: Package },
  { label: "Cartons reconditionnés", to: "/cartons-reconditionnes", Icon: Recycle },
  { label: "Emballage et protection", to: "/emballage-protection", Icon: ShieldCheck },
  { label: "Fermeture et accessoires", to: "/fermeture-accessoires", Icon: Scissors },
  { label: "Kit cartons déménagement", to: "/packs-cartons", Icon: Layers },
];

/**
 * Sticky sous-menu de catégories.
 * Desktop (≥ md) : 5 boutons en ligne.
 * Mobile : un seul bouton avec la catégorie courante + flèche, qui ouvre la liste au clic.
 */
const CategoriesSubnav = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const current =
    CATEGORIES.find((c) => c.to === pathname) || CATEGORIES[0];

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("touchstart", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("touchstart", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // Desktop button (same as before)
  const renderDesktopItem = ({ label, to, Icon }) => {
    const testId = `subnav-${label.toLowerCase().replace(/\s+/g, "-")}`;
    const isActive = to === pathname;

    const baseClass =
      "group flex-1 min-w-0 inline-flex items-center justify-center gap-2.5 px-4 py-3 rounded-2xl text-[13.5px] font-bold transition-all duration-300 text-center whitespace-nowrap";
    const activeClass =
      "bg-[#237cb5] text-white shadow-[0_10px_25px_-12px_rgba(35,124,181,0.6)] -translate-y-0.5";
    const idleClass =
      "bg-white text-[#0f1d2b] border border-[#cfdbe6] hover:bg-[#237cb5] hover:text-white hover:border-[#237cb5] hover:-translate-y-0.5 hover:shadow-[0_10px_25px_-12px_rgba(35,124,181,0.6)]";

    return (
      <Link
        key={label}
        to={to}
        className={`${baseClass} ${isActive ? activeClass : idleClass}`}
        data-testid={testId}
      >
        <span
          className={`flex items-center justify-center h-7 w-7 rounded-full shrink-0 transition-colors ${
            isActive
              ? "bg-white/20 text-white"
              : "bg-[#eaf3fb] text-[#237cb5] group-hover:bg-white/20 group-hover:text-white"
          }`}
        >
          <Icon className="h-4 w-4" strokeWidth={2.4} />
        </span>
        <span className="truncate">{label}</span>
      </Link>
    );
  };

  return (
    <div
      className="sticky top-16 md:top-20 z-30 border-b border-[#cfdbe6] bg-[#EEF3F7]"
      data-testid="categories-subnav"
    >
      <div className="container-cdm">
        {/* ===== Mobile : dropdown ===== */}
        <div className="md:hidden py-2.5 relative" ref={wrapRef}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-haspopup="listbox"
            className="w-full inline-flex items-stretch justify-between gap-3 pl-3 pr-1.5 py-1.5 rounded-xl bg-white border border-[#cfdbe6] text-[#0f1d2b] shadow-sm active:bg-[#f7fafc] [-webkit-tap-highlight-color:transparent]"
            data-testid="categories-subnav-mobile-trigger"
          >
            <span className="inline-flex items-center gap-2.5 min-w-0 py-0.5">
              <span className="flex items-center justify-center h-8 w-8 rounded-full bg-[#237cb5] text-white shrink-0">
                <current.Icon className="h-4 w-4" strokeWidth={2.4} />
              </span>
              <span className="truncate text-[14px] font-extrabold text-[#0f1d2b]">
                {current.label}
              </span>
            </span>
            <span
              className={`shrink-0 flex items-center justify-center h-9 w-9 self-center rounded-lg bg-[#eaf3fb] border border-[#cfdbe6] text-[#237cb5] transition-transform duration-200 ${
                open ? "rotate-180" : ""
              }`}
            >
              <ChevronDown className="h-5 w-5" strokeWidth={3} />
            </span>
          </button>

          {open && (
            <div
              role="listbox"
              data-testid="categories-subnav-mobile-list"
              className="absolute left-0 right-0 mt-2 bg-white border border-[#cfdbe6] rounded-2xl shadow-[0_18px_40px_-12px_rgba(15,29,43,0.25)] overflow-hidden z-40"
            >
              {CATEGORIES.map(({ label, to, Icon }) => {
                const isActive = to === pathname;
                const testId = `subnav-mobile-${label
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`;
                return (
                  <Link
                    key={label}
                    to={to}
                    role="option"
                    aria-selected={isActive}
                    onClick={() => setOpen(false)}
                    className={`flex items-center justify-between gap-3 px-3.5 py-3 text-[13.5px] font-bold border-b border-[#eef2f6] last:border-b-0 [-webkit-tap-highlight-color:transparent] ${
                      isActive
                        ? "bg-[#f0f6fb] text-[#237cb5]"
                        : "text-[#0f1d2b] active:bg-[#f7fafc]"
                    }`}
                    data-testid={testId}
                  >
                    <span className="inline-flex items-center gap-2.5 min-w-0">
                      <span
                        className={`flex items-center justify-center h-7 w-7 rounded-full shrink-0 ${
                          isActive
                            ? "bg-[#237cb5] text-white"
                            : "bg-[#eaf3fb] text-[#237cb5]"
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5" strokeWidth={2.4} />
                      </span>
                      <span className="truncate">{label}</span>
                    </span>
                    {isActive && (
                      <Check className="h-4 w-4 text-[#237cb5] shrink-0" strokeWidth={2.6} />
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* ===== Desktop : 5 buttons in a row ===== */}
        <div className="hidden md:flex items-stretch gap-3 py-4">
          {CATEGORIES.map(renderDesktopItem)}
        </div>
      </div>
    </div>
  );
};

export default CategoriesSubnav;
